import React from "react";
import { useAuth } from "shared/context/auth";
import PdfTemplateName from "shared/enums/PdfTemplateName";
import { useGetEnabledFeaturesDetails } from "./dataloader";
import InventoryType from "../../../../shared/enums/InventoryType";
import FeatureType from "../../../../shared/enums/FeatureType";
import ApiEnabledFeatures from "../../../../shared/types/ApiEnabledFeatures";
import ContextEntityType from "../../../../shared/enums/ContextEntityType";
import ContactMode from "../../../../shared/enums/ContactMode";
import { useParticipantDetails } from "../participantDetails";
import AIFeature from "shared/enums/AIFeature";

export default function useEnabledFeatureContextProviderService() {
  const { userDetail, isLoggedIn } = useAuth();
  const { participantDetails } = useParticipantDetails();
  const { data: enabledFeatureList } = useGetEnabledFeaturesDetails({
    registrationParticipantId: participantDetails?.registrationParticipantId
  });

  const isFeatureEnabled = React.useCallback(
    (obj: ApiEnabledFeatures) => {
      return Boolean(
        enabledFeatureList?.find(
          item =>
            item.type === obj.type &&
            item.name === obj.name &&
            item.enabled === obj.enabled &&
            item.contextType === obj.contextType &&
            (obj.contextId ? item.contextId === obj.contextId : true)
        )
      );
    },
    [enabledFeatureList]
  );

  const isPDFTemplateEnabled = React.useCallback(
    (templateName: PdfTemplateName) => {
      return isFeatureEnabled({
        type: FeatureType.PDF_TEMPLATES,
        enabled: true,
        name: templateName,
        contextType: ContextEntityType.REGISTRATION,
        contextId: userDetail?.registrationId
      });
    },
    [isFeatureEnabled, userDetail?.registrationId]
  );

  const isAddOnInventoryEnabled = React.useCallback(
    (eventId: string) => {
      return isFeatureEnabled({
        type: FeatureType.INVENTORY,
        enabled: true,
        name: InventoryType.ADD_ONS,
        contextType: ContextEntityType.EVENT,
        contextId: eventId
      });
    },
    [isFeatureEnabled]
  );

  const isMyPrivilegesEnabled = React.useMemo(() => {
    return isFeatureEnabled({
      type: FeatureType.CONTACT_MODE,
      enabled: true,
      name: ContactMode.ACCESS_CONTROL,
      contextType: ContextEntityType.EVENT
    });
  }, [isFeatureEnabled]);

  const isPostEventSummaryEnabled = React.useCallback(
    (eventId: string) => {
      return isFeatureEnabled({
        type: FeatureType.PDF_TEMPLATES,
        enabled: true,
        name: PdfTemplateName.POST_EVENT_SUMMARY,
        contextType: ContextEntityType.EVENT,
        contextId: eventId
      });
    },
    [isFeatureEnabled]
  );

  const isAITextEnhancementEnabled = React.useMemo(() => {
    return isFeatureEnabled({
      type: FeatureType.AI_FEATURE,
      enabled: true,
      name: AIFeature.TEXT_ENHANCEMENT,
      contextType: ContextEntityType.REGISTRATION,
      contextId: userDetail?.registrationId
    });
  }, [isFeatureEnabled, userDetail?.registrationId]);

  return {
    enabledFeatureList,
    isPDFTemplateEnabled,
    isAddOnInventoryEnabled,
    isMyPrivilegesEnabled,
    isLoggedIn,
    isPostEventSummaryEnabled,
    isAITextEnhancementEnabled
  };
}
